import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, t } from '../../context/LanguageContext';
import driverPortalService from '../../services/driverPortalService';

const LOCATION_PING_MS = 15000;

function ActiveDeliveryCard({ delivery, onPickedUp, lang }) {
  const navigate = useNavigate();
  const [markingUp, setMarkingUp] = useState(false);
  const intervalRef = useRef(null);

  // Periodic GPS ping while out for delivery, so the customer's tracking page stays fresh.
  useEffect(() => {
    if (delivery.status !== 'picked_up') return;
    const ping = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        pos => driverPortalService.pingLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {}),
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    };
    ping();
    intervalRef.current = setInterval(ping, LOCATION_PING_MS);
    return () => clearInterval(intervalRef.current);
  }, [delivery.status]);

  const handlePickedUp = async () => {
    setMarkingUp(true);
    try {
      await onPickedUp(delivery.id);
    } finally {
      setMarkingUp(false);
    }
  };

  const deliveryAddress = [delivery.Order?.deliveryAddressLine1, delivery.Order?.deliveryCity, delivery.Order?.deliveryState, delivery.Order?.deliveryZip].filter(Boolean).join(', ');

  return (
    <div className="card shadow-sm mb-3" style={{ border: '2px solid #0d6efd' }}>
      <div className="card-body" style={{ padding: '14px' }}>
        <div className="d-flex justify-content-between align-items-start mb-1">
          <div className="fw-bold" style={{ fontSize: 15 }}>#{delivery.Order?.orderNumber}</div>
          <span className="badge bg-primary text-capitalize" style={{ fontSize: 12 }}>{delivery.status.replace('_', ' ')}</span>
        </div>
        <div style={{ fontSize: 13, color: '#495057' }}>
          {delivery.Order?.deliveryContactName || t('No name on file', 'Sin nombre', lang)}
          {delivery.Order?.deliveryPhone && <> · <a href={`tel:${delivery.Order.deliveryPhone}`}>{delivery.Order.deliveryPhone}</a></>}
        </div>
        <div style={{ fontSize: 13, color: '#495057' }}>
          {[delivery.Order?.deliveryAddressLine1, delivery.Order?.deliveryAddressLine2, delivery.Order?.deliveryCity, delivery.Order?.deliveryState, delivery.Order?.deliveryZip].filter(Boolean).join(', ') || t('Address not available', 'Dirección no disponible', lang)}
        </div>
        {deliveryAddress && (
          <a
            className="btn btn-outline-primary btn-sm mt-2"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(deliveryAddress)}`}
            target="_blank" rel="noopener noreferrer"
          >
            🧭 {t('Open in Maps', 'Abrir en Mapas', lang)}
          </a>
        )}
        {delivery.etaToFactoryMinutes != null && delivery.status === 'accepted' && (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            {t('ETA to factory', 'ETA a la fábrica', lang)}: ~{delivery.etaToFactoryMinutes} {t('min', 'min', lang)}
            {delivery.distanceToFactoryMeters != null && ` (${(delivery.distanceToFactoryMeters / 1609.34).toFixed(1)} mi)`}
          </div>
        )}
        {delivery.Order?.deliveryMethod && (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {t('Delivery method', 'Método de entrega', lang)}: {delivery.Order.deliveryMethod}
          </div>
        )}
        <div className="fw-bold text-success mt-1" style={{ fontSize: 14 }}>${Number(delivery.payoutAmountUsd || 0).toFixed(2)}</div>

        {delivery.status === 'accepted' && (
          <button className="btn btn-primary w-100 mt-2" onClick={handlePickedUp} disabled={markingUp}>
            {markingUp ? <span className="spinner-border spinner-border-sm" /> : t('Mark Picked Up', 'Marcar Recogido', lang)}
          </button>
        )}
        {delivery.status === 'picked_up' && (
          <button className="btn btn-success w-100 mt-2" onClick={() => navigate(`/portal/deliveries/${delivery.orderId}/pod`)}>
            {t('Complete Delivery (Capture Proof)', 'Completar Entrega (Capturar Prueba)', lang)}
          </button>
        )}
      </div>
    </div>
  );
}

export default function PortalDriverDeliveriesPage() {
  const { lang } = useLanguage();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    driverPortalService.getMyDeliveries().then(setDeliveries).catch(() => setError('Failed to load deliveries.')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePickedUp = async (deliveryId) => {
    try {
      await driverPortalService.markPickedUp(deliveryId);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to mark picked up.');
    }
  };

  if (loading) return <div className="text-center py-5"><span className="spinner-border spinner-border-sm" /></div>;

  const active = deliveries.filter(d => ['accepted', 'picked_up'].includes(d.status));
  const past = deliveries.filter(d => ['delivered', 'cancelled'].includes(d.status));

  return (
    <div>
      {/* Sticky under the portal's own top bar (also sticky, 0-56px) so the driver always has the
          active delivery + nav in view while scrolling a long history list below. */}
      <div style={{ position: 'sticky', top: 56, zIndex: 90, background: '#f4f6fa', paddingTop: 2 }}>
        <h6 className="fw-bold mb-3">{t('My Deliveries', 'Mis Entregas', lang)}</h6>
        {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
        {active.map(d => <ActiveDeliveryCard key={d.id} delivery={d} onPickedUp={handlePickedUp} lang={lang} />)}
      </div>

      {active.length === 0 && past.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40, marginBottom: 8 }}>🚚</div>
          <p style={{ fontSize: 14 }}>{t('No deliveries yet — check Offers to accept one.', 'Aún no hay entregas — revisa Ofertas para aceptar una.', lang)}</p>
        </div>
      ) : (
        <>
          {past.length > 0 && (
            <>
              <h6 className="fw-bold mt-4 mb-2" style={{ fontSize: 13 }}>{t('History', 'Historial', lang)}</h6>
              <div className="d-flex flex-column gap-2">
                {past.map(d => (
                  <div key={d.id} className="card" style={{ border: '1px solid #dee2e6' }}>
                    <div className="card-body d-flex justify-content-between align-items-center" style={{ padding: '10px 14px' }}>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>#{d.Order?.orderNumber}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{d.Order?.deliveryCity || '—'}</div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold" style={{ fontSize: 13 }}>${Number(d.payoutAmountUsd || 0).toFixed(2)}</div>
                        <span className={`badge ${d.payoutStatus === 'paid' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 10 }}>
                          {d.payoutStatus === 'paid' ? t('Paid', 'Pagado', lang) : t('Pending', 'Pendiente', lang)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
